import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Eye, EyeOff, Square, Circle, Type, Image as ImageIcon, MoreVertical, ChevronsUpDown, Layers, Lock, X, Component } from 'lucide-react';
import { Button } from '@/ui-components/ui/button';
import { cn } from '@/lib/utils/cn';
import { Element, TextElement } from '../../core/template.schemas';
import { resolveTextContent } from '../../core/runtime/resolve';

import { 
  useTemplateStore, 
  useSelectedElementId,
  useSortedElements
} from '../hooks/useTemplateStore';
import { usePermissions } from '@/lib/hooks/use-permissions';

import { ElementDropdownMenu } from './element-context-menu';
import { Permission } from '@/lib/api/interfaces/permissions.interface';

interface LayersPanelProps {
  className?: string;
  onClose?: () => void;
  showCloseButton?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onOpenSlotPanel?: () => void;
}

const getElementIcon = (element: string) => {
  switch (element) {
    case 'rect': return <Square className="w-3 h-3" />;
    case 'circle': return <Circle className="w-3 h-3" />;
    case 'text': return <Type className="w-3 h-3" />;
    case 'image': return <ImageIcon className="w-3 h-3" />;
    case 'svg': return <Component className="w-3 h-3" />;
    default: return <Square className="w-3 h-3" />;
  }
};

const getElementName = (element: Element) => {
  // If custom name is set, use it
  if (element.name && element.name.trim() !== '') {
    return element.name;
  }
  
  // Otherwise use default naming
  switch (element.type) {
    case 'text': {
      const textElement = element as TextElement;
      // Resolve the content to get display text
      const resolveContext = { selected: {}, overrides: {} }; // TODO: Get actual context from store
      const resolvedText = resolveTextContent(textElement.content, resolveContext);
      return resolvedText || 'Text';
    }
    case 'rect': return 'Rechteck';
    case 'circle': return 'Kreis';
    case 'image': return 'Bild';
    case 'svg': return 'Vektorgrafik';
    default: return element.type;
  }
};

export const LayersPanel: React.FC<LayersPanelProps> = ({ 
  className, 
  onClose, 
  showCloseButton = false,
  onDragStart,
  onDragEnd,
  onOpenSlotPanel
}) => {
  const elements = useSortedElements();
  const selectedElementId = useSelectedElementId();
  const selectElement = useTemplateStore(s => s.selectElement);
  const toggleElementVisibility = useTemplateStore(s => s.toggleElementVisibility);
  const reorderElements = useTemplateStore(s => s.reorderElements);
  const updateElement = useTemplateStore(s => s.updateElement);
  const { can } = usePermissions();
  
  // State for inline editing
  const [editingElementId, setEditingElementId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');  
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Focus input when editing starts
  useEffect(() => {
    if (editingElementId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingElementId]);
  
  const handleDragEnd = (result: DropResult) => {
    // Drag-End Callback aufrufen
    onDragEnd?.();
    
    if (!result.destination) return;
    
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    
    if (fromIndex !== toIndex) {
      const totalElements = elements.length;
      const actualFromIndex = totalElements - 1 - fromIndex;
      const actualToIndex = totalElements - 1 - toIndex;
      
      reorderElements(actualFromIndex, actualToIndex);
    }
  };
  
  const handleNameDoubleClick = (element: Element, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingElementId(element.id);
    setEditingName(getElementName(element));
  };
  
  const handleNameSubmit = () => {
    if (editingElementId && editingName.trim() !== '') {
      updateElement(editingElementId, { name: editingName.trim() });
    }
    setEditingElementId(null);
    setEditingName('');
  };
  
  const handleNameCancel = () => {
    setEditingElementId(null);
    setEditingName('');
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  return (
    <div className={cn("w-full bg-white h-full flex flex-col", className)}>
      {/* Header nur anzeigen wenn showCloseButton true ist (für Standalone-Nutzung) */}
      {showCloseButton && (
        <div className="px-3 py-3 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-gray-500" />
              <h2 className="text-sm font-medium text-gray-700">Ebenen</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-2 pb-2">
          <DragDropContext 
            onDragStart={(start) => {
              onDragStart?.();
            }}
            onDragEnd={handleDragEnd}
          >
            <Droppable droppableId="layers" direction="vertical">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-0.5 pt-2"
                >
                  {elements.map((element, index) => (
                    <Draggable 
                      key={element.id} 
                      draggableId={element.id} 
                      index={index}
                      isDragDisabled={element.isWatermark && !can(Permission.EDIT_WATERMARKS)}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={cn(
                            "rounded-md transition-all duration-150 cursor-pointer group",
                            selectedElementId === element.id
                              ? "bg-blue-50 ring-1 ring-blue-200"
                              : "hover:bg-gray-50",
                            snapshot.isDragging && "shadow-lg bg-white ring-1 ring-gray-200",
                            !element.visible && "opacity-40"
                          )}
                          onClick={(e) => {
                            // Prevent selection during drag
                            if (!snapshot.isDragging) {
                              selectElement(element.id);
                            }
                          }}
                          style={{
                            ...provided.draggableProps.style,
                            ...(snapshot.isDragging && {
                              transform: provided.draggableProps.style?.transform
                                ? provided.draggableProps.style.transform.replace(
                                    /translate\(([^,]+),\s*([^)]+)\)/,
                                    'translate(0px, $2)'
                                  )
                                : undefined
                            })
                          }}
                        >
                          <div className="px-2 py-1.5">
                            <div className="flex items-center gap-2">
                              {/* Drag Handle - Visual indicator only */}
                              <div
                                className="cursor-grab active:cursor-grabbing flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <ChevronsUpDown className="w-3 h-3" />
                              </div>
                              
                              {/* Element Icon */}
                              <div className="text-gray-500 flex-shrink-0">
                                {getElementIcon(element.type)}
                              </div>
                              
                              {/* Lock Icon */}
                              {(element.locked || (element.isWatermark && !can(Permission.EDIT_WATERMARKS))) && (
                                <div className="text-gray-400 flex-shrink-0">
                                  <Lock className="w-3 h-3" />
                                </div>
                              )}
                              
                              {/* Element Info */}
                              <div className="flex-1 min-w-0">
                                {editingElementId === element.id ? (
                                  <input
                                    ref={inputRef}
                                    type="text"
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onBlur={handleNameSubmit}
                                    onKeyDown={handleKeyDown}
                                    className="text-xs font-medium text-gray-800 bg-white border border-blue-300 rounded px-1 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                ) : (
                                  <div 
                                    className="text-xs font-medium text-gray-800 truncate cursor-text rounded px-1 py-0.5 -mx-1 -my-0.5"
                                    onDoubleClick={(e) => handleNameDoubleClick(element, e)}
                                  >
                                    {getElementName(element)}
                                  </div>
                                )}
                              </div>
                              
                              {/* Controls - only visible on hover or when selected */}
                              <div className={cn(
                                "flex items-center gap-1 transition-opacity",
                                selectedElementId === element.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                              )}>
                                {/* Visibility Toggle */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 flex-shrink-0 hover:bg-gray-200/60"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleElementVisibility(element.id);
                                  }}
                                >
                                  {!element.visible ? (
                                    <EyeOff className="w-3 h-3 text-gray-400" />
                                  ) : (
                                    <Eye className="w-3 h-3 text-gray-500" />
                                  )}
                                </Button>
                                
                                {/* More Options */}
                                <ElementDropdownMenu elementId={element.id}>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 flex-shrink-0 hover:bg-gray-200/60"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="w-3 h-3 text-gray-400" />
                                  </Button>
                                </ElementDropdownMenu>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  
                  {elements.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-xs">
                      Keine Elemente vorhanden
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </div>
  );
};
